/**
 * product_metadata 统一可售派生（admin 侧）——与 C 端 `product-metadata-serveability.ts`（novel-app-server）
 * 同一套 IAP 基线 + Stripe 派生规则的**超集**：C 端只需 boolean `is_serveable`（首购/新购 gate），
 * admin 额外为运营 UI 产出 `derived_minimum_*` 派生下限 + `validation_issues[]` 不可售原因闭集。
 *
 * 契约源：dev-docs `02-API契约/A8-运营支付.md` §1-2 + `03-数据字典/70-产品配置.md` §2（#465 承接 #461）。
 *
 * 不变量：`is_serveable === (validation_issues.length === 0)`，且**必须与 C 端 `isProductMetadataServeable`
 * 逐例等价**（同 App 候选集 + apps.third_party_bonus_ratio 输入）——admin 预测的可售态即 C 端实际会 serve 的态。
 * 全程 bigint 精确运算，仅在最终增量处 `ceil`，禁 JS 浮点参与权益下限计算。
 *
 * **原因码与 C 端根因码的对应关系**写成数据：`UNSERVEABLE_REASON_PROJECTION`。
 * C 端 `PRODUCT_UNSERVEABLE_REASONS` 每个键必须在该表有归属；跨仓闸
 * `check-serveability-parity` 按表对账，注释不再当事实源。
 *
 * **「履约未实现」闸的镜像**（C 端 `FULFILLMENT_NOT_IMPLEMENTED_EFFECTS`）：效果登记进 §2.5 只说明
 * 「契约认得它」，可售判定还要问「平台发不发得出货」。C 端把未实现履约的效果判**不可售**，本仓必须
 * 同判 —— 否则运营配好买断产品、后台显示可售、C 端永不出现，且无原因码可解释。布尔逐例等价；
 * 原因码走 A8 §1 专用值 `EFFECT_FULFILLMENT_NOT_IMPLEMENTED`（**不**借 `PRODUCT_FIELDS_INVALID`：
 * 后者指向「改本行」，本值指向「不必改，等平台」）。与本行形态是否合法**独立判定**，形态问题并存时
 * 两值同在数组里。不进写侧 / activate 阻断集（`NON_BLOCKING_VALIDATION_ISSUES`）。
 * 原生 App 的 `VIP_READING` 订阅同走本值：70 §2「只有已登记并实现履约的 VIP 可售」，C 端原生订阅履约只承接
 * `COIN_PERIOD_GRANT`，形态合法的 VIP 行在 C 端判同一根因（`EFFECT_FULFILLMENT_NOT_IMPLEMENTED`）。
 *
 * **订阅效果**（70 §2 `subscription_effect`）：存储值经 `normalizeSubscriptionEffect` 归一后判定 —— 存量
 * `COIN_DAILY_BONUS` 与 `COIN_PERIOD_GRANT` 同一判据（C 端同名归一）。冻结列 `daily_grant_amount` 不进入
 * 任何效果的谓词，故不在候选字段里出现。
 * **基线资格不看履约进度**（`hasValidProductShape` 只跑形态层）：C 端把本闸并进形态校验，于是
 * STRIPE 买断行会拿到「缺 IAP 基线」；本仓让它拿到履约码本身 —— 「缺基线」会把运营指去建一条
 * 其实已存在的 IAP 行，且那个值是写侧阻断集成员，会把「配得出、暂不可售」变成配不出。
 */

import {
  type SettlementChannel,
  isFrameworkSettlementChannel,
  isIapPairedSettlementChannel,
} from './payment-methods';
import {
  type ConsumableEffect,
  type ProductType,
  type SubscriptionPeriod,
  isReadingPassTierId,
  isZeroEntitlementEffect,
  normalizeSubscriptionEffect,
} from './product-metadata';

/** 不可售原因闭集（dev-docs A8 §1 `validation_issues`）；可售时数组为空 */
export const PRODUCT_VALIDATION_ISSUES = [
  'INACTIVE',
  'ACCESS_LEVEL_ID_MISSING',
  'COIN_AMOUNT_NON_POSITIVE',
  'PRODUCT_FIELDS_INVALID',
  'IAP_BASELINE_MISSING',
  'COIN_MISMATCH',
  'PRODUCT_FIELDS_MISMATCH',
  'BELOW_MINIMUM_BONUS',
  'UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD',
  'READING_PASS_TIER_INCOMPLETE',
  'CHANNEL_PRICE_UNSELECTABLE',
  'EFFECT_FULFILLMENT_NOT_IMPLEMENTED',
] as const;

/**
 * 写侧 / activate **不**硬拒的不可售原因（读侧仍出现在 `validation_issues`，该行仍不可售）：
 * - `READING_PASS_TIER_INCOMPLETE`：齐全性执行者是 `is_serveable` validator，先配一位必然命中。
 * - `EFFECT_FULFILLMENT_NOT_IMPLEMENTED`：缺的是平台发货能力（含原生 `VIP_READING`），运营改本行消不掉。
 * - `CHANNEL_PRICE_UNSELECTABLE`：价格经 A8 §10.3 PUT 单独配置，且该端点要求商品行已存在；§5 activate gate
 *   的阻断条件不含选价，先建行 / 上架、后配价格是合法顺序。
 */
export const NON_BLOCKING_VALIDATION_ISSUES: readonly ProductValidationIssue[] = [
  'READING_PASS_TIER_INCOMPLETE',
  'CHANNEL_PRICE_UNSELECTABLE',
  'EFFECT_FULFILLMENT_NOT_IMPLEMENTED',
];
export type ProductValidationIssue = (typeof PRODUCT_VALIDATION_ISSUES)[number];

/**
 * 不由 C 端 `PRODUCT_UNSERVEABLE_REASONS` 投影、由 admin 在统一 validator 之外另行派生的原因：
 * - `CHANNEL_PRICE_UNSELECTABLE`：C 端可售判定不读价格，选不出价格的框架通道候选在 discovery 剔除
 *   （`applyChannelPriceSelectability`）。可售闭集对账据此把它排除在「每个原因都有 C 端根因」之外。
 */
export const ADMIN_DERIVED_VALIDATION_ISSUES: readonly ProductValidationIssue[] = [
  'CHANNEL_PRICE_UNSELECTABLE',
];

/**
 * C 端 `PRODUCT_UNSERVEABLE_REASONS` 键 → admin `validation_issues` 的归并。
 *
 * - `issue`：该根因投影到列出的 admin 值（一对多是契约要求的归并，不是缺口）。
 * - `not_projected`：admin 判定面碰不到，必须写清原因。缺这条就会把「C 端新增根因」
 *   静默当成已覆盖。
 *
 * 键名必须与 C 端对象的键逐字相同。增删由 `check-serveability-parity` 对 `origin/main` 红灯。
 */
export type UnserveableReasonProjection =
  | { readonly kind: 'issue'; readonly issues: readonly ProductValidationIssue[] }
  | { readonly kind: 'not_projected'; readonly because: string };

export const UNSERVEABLE_REASON_PROJECTION = {
  INACTIVE: { kind: 'issue', issues: ['INACTIVE'] },
  SOFT_DELETED: {
    kind: 'not_projected',
    because: 'admin 查询默认过滤 deleted_at，软删行不进 is_serveable validator',
  },
  MISSING_ACCESS_LEVEL: { kind: 'issue', issues: ['ACCESS_LEVEL_ID_MISSING'] },
  INVALID_AMOUNTS: {
    kind: 'issue',
    issues: ['COIN_AMOUNT_NON_POSITIVE', 'PRODUCT_FIELDS_INVALID'],
  },
  INVALID_CONSUMABLE_SHAPE: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  INVALID_SUBSCRIPTION_SHAPE: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  UNKNOWN_PRODUCT_TYPE: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  UNSUPPORTED_PAYMENT_METHOD: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  MISSING_IAP_BASELINE: { kind: 'issue', issues: ['IAP_BASELINE_MISSING'] },
  AMBIGUOUS_IAP_BASELINE: { kind: 'issue', issues: ['IAP_BASELINE_MISSING'] },
  BASELINE_TYPE_MISMATCH: { kind: 'issue', issues: ['PRODUCT_FIELDS_MISMATCH'] },
  COIN_MISMATCH: { kind: 'issue', issues: ['COIN_MISMATCH'] },
  INVALID_BONUS_RATIO: { kind: 'issue', issues: ['BELOW_MINIMUM_BONUS'] },
  BONUS_BELOW_THIRD_PARTY_FLOOR: { kind: 'issue', issues: ['BELOW_MINIMUM_BONUS'] },
  SUBSCRIPTION_PERIOD_UNSUPPORTED: {
    kind: 'issue',
    issues: ['UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD'],
  },
  // 订阅效果 / 周期是 product 级字段（70 §2 维度列），跨通道不一致归 A8 §1 的 PRODUCT_FIELDS_MISMATCH
  SUBSCRIPTION_MISMATCH_WITH_BASELINE: { kind: 'issue', issues: ['PRODUCT_FIELDS_MISMATCH'] },
  MISSING_CONSUMABLE_EFFECT: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  UNKNOWN_CONSUMABLE_EFFECT: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  INVALID_EFFECT_PARAMS: { kind: 'issue', issues: ['PRODUCT_FIELDS_INVALID'] },
  EFFECT_MISMATCH_WITH_BASELINE: { kind: 'issue', issues: ['PRODUCT_FIELDS_MISMATCH'] },
  EFFECT_PARAMS_MISMATCH: { kind: 'issue', issues: ['PRODUCT_FIELDS_MISMATCH'] },
  READING_PASS_TIER_INCOMPLETE: { kind: 'issue', issues: ['READING_PASS_TIER_INCOMPLETE'] },
  EFFECT_FULFILLMENT_NOT_IMPLEMENTED: {
    kind: 'issue',
    issues: ['EFFECT_FULFILLMENT_NOT_IMPLEMENTED'],
  },
} as const satisfies Record<string, UnserveableReasonProjection>;

/**
 * 已在 §2.5 登记、形态也校验得了，但**平台尚未实现履约**的效果（C 端同名常量的镜像）。
 *
 * `CONSUMABLE_EFFECTS` 回答「契约登记了吗」，本集合回答「发得出货吗」—— 可售判定要的是后者。
 * C 端结算侧不看效果、也不看 scenario，`is_serveable` 是唯一挡得住「收钱不发货」的闸；admin 的
 * `is_serveable` 是对该闸的预测，故必须同步登记，否则后台显示可售而 C 端永不 serve。
 *
 * 当前三个登记效果均已实现（`NOVEL_PURCHASE` 由 dev-docs#729 翻「履约实现状态」，
 * C 端 origin/main 已摘闸）。本集合与 `isFulfillmentNotImplementedEffect` 按契约常驻：
 * **不得因当前取「未实现」的效果集合为空而摘除该判定**。新效果登记时先入本集合，履约落地后再删。
 */
export const FULFILLMENT_NOT_IMPLEMENTED_EFFECTS =
  [] as const satisfies readonly ConsumableEffect[];

/**
 * 「履约未实现」原因码（A8 §1 专用值）。运营动作是「不必改本行，等平台」—— 与
 * `PRODUCT_FIELDS_INVALID`（改本行）互斥，不得混报为字段错误。
 */
export const FULFILLMENT_NOT_IMPLEMENTED_ISSUE: ProductValidationIssue =
  'EFFECT_FULFILLMENT_NOT_IMPLEMENTED';

/** 该效果是否已登记但平台尚未实现履约（形态合法仍判不可售）*/
export function isFulfillmentNotImplementedEffect(
  effect: ConsumableEffect | string | null,
): boolean {
  return (
    effect !== null && (FULFILLMENT_NOT_IMPLEMENTED_EFFECTS as readonly string[]).includes(effect)
  );
}

/** 参与可售判定的最小行投影（金额一律 bigint minor units；service 从 string 转入）*/
export type ProductServeabilityCandidate = {
  accessLevelId: string | null;
  /** 行所属**结算通道**（`product_metadata` 按通道建模，§3 两层闭集）*/
  paymentMethod: SettlementChannel;
  productType: ProductType;
  /**
   * 订阅效果**存储值**（70 §2；CONSUMABLE 为 null）。不在投影层归一：判定入口统一经
   * normalizeSubscriptionEffect，未登记值原样进来才能被判不可售。
   */
  subscriptionEffect: string | null;
  subscriptionPeriod: SubscriptionPeriod | null;
  coinAmount: bigint;
  bonusAmount: bigint;
  /** §2.5 效果标识；CONSUMABLE 行为 null = 履约无从分派 → 不可售（server #561）*/
  consumableEffect: ConsumableEffect | string | null;
  /** §2.5 逐效果参数（jsonb 原样；非 object 一律按不合 shape 处理）*/
  consumableEffectParams: unknown;
  isActive: boolean;
};

export type ProductServeabilityContext = {
  /** 同一 App 下全部未软删候选行（含 IAP 基线与被评估的 STRIPE 行）*/
  candidates: readonly ProductServeabilityCandidate[];
  /** apps.third_party_bonus_ratio（decimal 串，如 "0.3500"）*/
  thirdPartyBonusRatio: string;
};

export type ProductServeabilityResult = {
  is_serveable: boolean;
  /**
   * STRIPE CONSUMABLE（发币）与 STRIPE `COIN_PERIOD_GRANT` SUBSCRIPTION 当前派生 bonus 下限（bigint string，
   * 同一公式 `IAP bonus + ceil(IAP coin × ratio)`，与订阅周期长度无关）；其他行 / 无基线 → null
   */
  derived_minimum_bonus_amount: string | null;
  validation_issues: ProductValidationIssue[];
  /**
   * 本行 `consumable_effect` 命中履约未实现集合（与形态是否合法独立）。**不是 A8 §1 响应字段**，
   * 写门禁已改读 `NON_BLOCKING_VALIDATION_ISSUES`（含 `EFFECT_FULFILLMENT_NOT_IMPLEMENTED`）；
   * 本位置位只供测试 / 内部诊断，形态错时仍为 true（独立判定），阻断靠并存的形态码。
   */
  fulfillment_not_implemented: boolean;
};

const SUBSCRIPTION_PERIOD_SET = new Set<SubscriptionPeriod>([
  'WEEKLY',
  'MONTHLY',
  'QUARTERLY',
  'YEARLY',
]);

/**
 * 原生 STRIPE 订阅（`COIN_PERIOD_GRANT` / `VIP_READING` 两种效果）与 `VIP_READING` 形态只允许的周期
 * （70 §2「原生 Stripe 订阅周期约束」）；QUARTERLY / YEARLY 保留给 IAP 发币订阅，进 STRIPE 即 fail-closed。
 */
export const STRIPE_SUBSCRIPTION_PERIODS = [
  'WEEKLY',
  'MONTHLY',
] as const satisfies readonly SubscriptionPeriod[];

export function isStripeSubscriptionPeriod(period: string | null | undefined): boolean {
  return (STRIPE_SUBSCRIPTION_PERIODS as readonly (string | null | undefined)[]).includes(period);
}

function isNonBlank(value: string | null): value is string {
  return value !== null && value.trim().length > 0;
}

/** 权益时长上界 = C 端库存列 `reading_pass_cards.duration_seconds` 的 int4 上限（与写侧校验同源）*/
const MAX_READING_PASS_DURATION_SECONDS = 2147483647;

function asParamsObject(params: unknown): Record<string, unknown> | null {
  return typeof params === 'object' && params !== null && !Array.isArray(params)
    ? (params as Record<string, unknown>)
    : null;
}

/** 产品位身份；非空无空白即合法（新 UUID v7 + 存量 CARD_A/CARD_B）；空白 / 非字符串 → null */
export function readingPassTierIdOf(
  row: Pick<ProductServeabilityCandidate, 'consumableEffect' | 'consumableEffectParams'>,
): string | null {
  if (row.consumableEffect !== 'READING_PASS_CARD') return null;
  const raw = asParamsObject(row.consumableEffectParams)?.tier_id;
  return isReadingPassTierId(raw) ? raw : null;
}

/** 逐 key 逐值比对两行效果参数（§2.5 跨通道一致性判据；与 C 端 haveIdenticalEffectParams 同判据）*/
function haveIdenticalEffectParams(
  left: ProductServeabilityCandidate,
  right: ProductServeabilityCandidate,
): boolean {
  const l = asParamsObject(left.consumableEffectParams);
  const r = asParamsObject(right.consumableEffectParams);
  if (l === null || r === null) return false;
  const lk = Object.keys(l).sort();
  const rk = Object.keys(r).sort();
  if (lk.length !== rk.length) return false;
  return lk.every((key, i) => key === rk[i] && l[key] === r[key]);
}

/** params 是对象且无任何 key（`COIN_GRANT` / `NOVEL_PURCHASE` 的 shape 判据）*/
function isEmptyParams(params: unknown): boolean {
  const object = asParamsObject(params);
  return object !== null && Object.keys(object).length === 0;
}

/**
 * CONSUMABLE 效果的完整可售判定 = 形态合法（`effect_wellformed`）**且**平台能履约（与 C 端
 * `findConsumableEffectViolation` 同结构）。两层分开求值，因为二者的可修复性不同：形态错运营改本行
 * 就好，履约未实现只能等平台 —— 后者不进写侧阻断集（`fulfillmentNotImplemented` 标出这一位）。
 */
function findConsumableEffectViolation(row: ProductServeabilityCandidate): {
  issues: ProductValidationIssue[];
  fulfillmentNotImplemented: boolean;
} {
  const issues: ProductValidationIssue[] = [];
  const shapeViolation = findConsumableEffectShapeViolation(row);
  if (shapeViolation !== null) issues.push(shapeViolation);
  // 形态合法 ≠ 发得出货。与形态**独立判定**：params 不合时两值并存（A8 §1 状态组合表）。
  const fulfillmentNotImplemented = isFulfillmentNotImplementedEffect(row.consumableEffect);
  if (fulfillmentNotImplemented) issues.push(FULFILLMENT_NOT_IMPLEMENTED_ISSUE);
  return { issues, fulfillmentNotImplemented };
}

/**
 * `effect_wellformed`（§2.5 逐效果谓词）——**CONSUMABLE 的权益金额判据由效果决定，不是一刀切
 * `coin_amount > 0`**：`READING_PASS_CARD` / `NOVEL_PURCHASE` 不发币、coin / bonus 恒 0，套用发币判据会把
 * 每一张阅读卡、每个买断产品都判成不可售。只看形态、不问平台能否履约。
 *
 * **原因码映射**（A8 §1「取值互斥」表逐条裁定）：「`COIN_GRANT` 的 `coin_amount > 0`」有专用值
 * `COIN_AMOUNT_NON_POSITIVE`，谓词内其余各项（params shape / 恒零列 / 未登记效果值）归
 * `PRODUCT_FIELDS_INVALID`；「同 tier 配对通道齐全」由 `READING_PASS_TIER_INCOMPLETE` 单独承接，不在本函数
 * （见 `hasCompleteReadingPassTiers`）。
 */
function findConsumableEffectShapeViolation(
  row: ProductServeabilityCandidate,
): ProductValidationIssue | null {
  if (row.consumableEffect === null) return 'PRODUCT_FIELDS_INVALID';
  if (row.consumableEffect === 'COIN_GRANT') {
    if (row.coinAmount <= 0n) return 'COIN_AMOUNT_NON_POSITIVE';
    return isEmptyParams(row.consumableEffectParams) ? null : 'PRODUCT_FIELDS_INVALID';
  }
  if (row.consumableEffect === 'NOVEL_PURCHASE') {
    // §2.5：`coin=bonus=0 AND consumable_effect_params = '{}'::jsonb`。**产品不承载价格档** ——
    // 任何 key 都非法，价格档 → 产品的映射在 `app_novel_purchase_products`，买断哪一本由 novel_id 决定。
    return row.coinAmount === 0n &&
      row.bonusAmount === 0n &&
      isEmptyParams(row.consumableEffectParams)
      ? null
      : 'PRODUCT_FIELDS_INVALID';
  }
  if (row.consumableEffect === 'READING_PASS_CARD') {
    const duration = asParamsObject(row.consumableEffectParams)?.duration_seconds;
    const durationValid =
      typeof duration === 'number' &&
      Number.isSafeInteger(duration) &&
      duration > 0 &&
      duration <= MAX_READING_PASS_DURATION_SECONDS;
    return row.coinAmount === 0n &&
      row.bonusAmount === 0n &&
      readingPassTierIdOf(row) !== null &&
      durationValid
      ? null
      : 'PRODUCT_FIELDS_INVALID';
  }
  // 未在 §2.5 登记的效果值不构成可分派效果 —— 放它进 serving，履约时才 fail-loud 就晚了（钱已收）
  return 'PRODUCT_FIELDS_INVALID';
}

/** 将非负十进制比例精确转为分数，禁止 JS 浮点参与权益下限计算（与 C 端 parseNonNegativeDecimal 同实现）。*/
function parseNonNegativeDecimal(value: string): { numerator: bigint; denominator: bigint } | null {
  const match = /^(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return null;
  const fraction = match[2] ?? '';
  return {
    numerator: BigInt(`${match[1]}${fraction}`),
    denominator: 10n ** BigInt(fraction.length),
  };
}

/**
 * ceil(amount × ratio)，全程 bigint；非法输入返 null（与 C 端 ceilProductRatio 逐字对齐）。
 * 不带周期天数除数：订阅下限按每计费周期发放量求值、与周期长度无关（70 §2 反模式：禁按 7 / 30 天换算）。
 */
export function ceilProductRatio(amount: bigint, ratio: string): bigint | null {
  if (amount < 0n) return null;
  const parsed = parseNonNegativeDecimal(ratio);
  if (!parsed) return null;
  const scaled = amount * parsed.numerator;
  return (scaled + parsed.denominator - 1n) / parsed.denominator;
}

/**
 * 单产品赠送比例由权益金额实时派生（响应派生字段，非 DB 列）：`bonus ÷ coin`，四位小数 ROUND_HALF_UP，
 * 非零结果最少保留两位小数；零值 "0"。CONSUMABLE 与 SUBSCRIPTION 同一口径（70 §2 `bonus_ratio`）——
 * `VIP_READING` 与不发币效果 coin / bonus 全零，自然得 "0"（与 C 端 deriveProductBonusRatio 对齐）。
 */
export function deriveProductBonusRatio(source: {
  coinAmount: bigint;
  bonusAmount: bigint;
}): string {
  if (source.bonusAmount <= 0n || source.coinAmount <= 0n) {
    return '0';
  }
  const scale = 10_000n;
  const scaledBonus = source.bonusAmount * scale;
  const quotient = scaledBonus / source.coinAmount;
  const remainder = scaledBonus % source.coinAmount;
  const rounded = quotient + (remainder * 2n >= source.coinAmount ? 1n : 0n);
  if (rounded === 0n) return '0';

  const integerPart = rounded / scale;
  let fractionPart = (rounded % scale).toString().padStart(4, '0');
  while (fractionPart.length > 2 && fractionPart.endsWith('0')) {
    fractionPart = fractionPart.slice(0, -1);
  }
  return `${integerPart}.${fractionPart}`;
}

/**
 * SUBSCRIPTION 逐效果谓词的**形态层**（70 §2 `effect_wellformed`），不含履约进度：
 * - `COIN_PERIOD_GRANT`（含存量 `COIN_DAILY_BONUS`）：`coin_amount > 0`（专用值 `COIN_AMOUNT_NON_POSITIVE`）
 *   且周期在 IAP 闭集内（否则 `PRODUCT_FIELDS_INVALID`）；bonus ≥ 0 由负金额守卫承接。
 * - `VIP_READING`：周期 WEEKLY / MONTHLY 且 coin / bonus 全零，否则 `PRODUCT_FIELDS_INVALID`；STRIPE 行
 *   周期非空但不在 WEEKLY / MONTHLY 内只归 `UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD`（由调用方追加）。
 * - NULL / 未登记值：`PRODUCT_FIELDS_INVALID`（不按名字或金额猜效果）。
 */
function findSubscriptionShapeViolations(
  row: ProductServeabilityCandidate,
): ProductValidationIssue[] {
  const effect = normalizeSubscriptionEffect(row.subscriptionEffect);
  if (effect === 'COIN_PERIOD_GRANT') {
    const issues: ProductValidationIssue[] = [];
    if (row.coinAmount <= 0n) issues.push('COIN_AMOUNT_NON_POSITIVE');
    if (row.subscriptionPeriod === null || !SUBSCRIPTION_PERIOD_SET.has(row.subscriptionPeriod)) {
      issues.push('PRODUCT_FIELDS_INVALID');
    }
    return issues;
  }
  if (effect === 'VIP_READING') {
    // 谓词要求 WEEKLY / MONTHLY。STRIPE 行周期非空却不在其内时，该条件由专用值
    // UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD 承接（A8 §1「每个失败条件恰由一个值承接」），此处不并报；
    // 缺周期仍是「product_type 与 subscription_period 不自洽」，两个通道都归 PRODUCT_FIELDS_INVALID。
    const periodCarriedByStripeIssue =
      row.paymentMethod === 'STRIPE' && row.subscriptionPeriod !== null;
    const periodOk =
      isStripeSubscriptionPeriod(row.subscriptionPeriod) || periodCarriedByStripeIssue;
    return periodOk && row.coinAmount === 0n && row.bonusAmount === 0n
      ? []
      : ['PRODUCT_FIELDS_INVALID'];
  }
  return ['PRODUCT_FIELDS_INVALID'];
}

/**
 * 原生 `VIP_READING` 订阅平台未实现履约（70 §2「只有已登记并实现履约的 VIP 可售」；C 端原生订阅履约只承接
 * `COIN_PERIOD_GRANT`）。与 CONSUMABLE 的履约未实现同一原因码、同一「不必改本行」语义，与形态独立判定。
 */
function isSubscriptionFulfillmentNotImplemented(row: ProductServeabilityCandidate): boolean {
  return normalizeSubscriptionEffect(row.subscriptionEffect) === 'VIP_READING';
}

/**
 * product 级字段自洽（IAP 基线资格判据）：不自洽 → PRODUCT_FIELDS_INVALID。
 * **`coin_amount > 0` 不再是所有行的前置**：CONSUMABLE 的金额判据下沉到逐效果谓词
 * （不发币效果 coin / bonus 恒 0），否则阅读卡 / 买断基线永远选不出来、配对 STRIPE 行恒 `IAP_BASELINE_MISSING`。
 *
 * **只跑形态层、不问履约进度**（与 C 端的唯一判据差异，文件头 ② 已登记）：履约未实现是平台维度、
 * 对 IAP / STRIPE 同时成立，让它参与基线选取只会把 STRIPE 行的原因码从「履约未实现」换成
 * 「缺 IAP 基线」—— 指着运营去建一条其实已经建好的行，且那个值是写侧阻断集成员。
 * 布尔结果不受影响：该 STRIPE 行自身照样命中履约闸而不可售。原生 `VIP_READING` 同理。
 */
function hasValidProductShape(row: ProductServeabilityCandidate): boolean {
  if (!row.isActive || !isNonBlank(row.accessLevelId) || row.bonusAmount < 0n) {
    return false;
  }
  if (row.productType === 'CONSUMABLE') {
    return row.subscriptionPeriod === null && findConsumableEffectShapeViolation(row) === null;
  }
  if (row.productType === 'SUBSCRIPTION') {
    return findSubscriptionShapeViolations(row).length === 0;
  }
  return false;
}

/** 唯一 active 且形状自洽的同 App 同 access_level IAP 基线；非唯一（0 或 >1）→ null */
function findUniqueIapBaseline(
  candidate: ProductServeabilityCandidate,
  candidates: readonly ProductServeabilityCandidate[],
): ProductServeabilityCandidate | null {
  const baselines = candidates.filter(
    (row) =>
      row.paymentMethod === 'IAP' &&
      row.accessLevelId === candidate.accessLevelId &&
      hasValidProductShape(row),
  );
  return baselines.length === 1 ? (baselines[0] ?? null) : null;
}

/**
 * 同一 `(app_id, tier_id)` 的配对通道齐全性（A8 §1 / 70 §2.5「卡位参与通道」，#773 / dev-docs#1513）：
 * 非 IAP 行（STRIPE / 已登记框架通道）以 IAP 行为配对，IAP 行以**任一**参与的非 IAP 行为配对；缺配对才报
 * `READING_PASS_TIER_INCOMPLETE`。**不要求最少两个卡位**，也不要求为未使用的结算通道建行 —— 单卡位
 * IAP + PAYERMAX（无 STRIPE 行）齐全即可售（与 C 端 `hasCompleteReadingPassTiers` 同判据，srv#1312）。
 *
 * **对侧要过「除齐全性外的全部判据」**：形态合法却与 IAP 基线 params 不一致的对侧不算配齐。
 * `tierId === null` → 无对侧可言，返 true 交由本行 `PRODUCT_FIELDS_INVALID` 表达。
 */
function hasCompleteReadingPassTiers(
  candidate: ProductServeabilityCandidate,
  context: ProductServeabilityContext,
): boolean {
  const tierId = readingPassTierIdOf(candidate);
  if (tierId === null) return true;
  const isCounterpart =
    candidate.paymentMethod === 'IAP'
      ? isIapPairedSettlementChannel
      : (paymentMethod: string) => paymentMethod === 'IAP';
  return context.candidates.some(
    (row) =>
      isCounterpart(row.paymentMethod) &&
      readingPassTierIdOf(row) === tierId &&
      evaluateExceptTierCompleteness(row, context).validation_issues.length === 0,
  );
}

/**
 * dev-docs `70-产品配置.md` §2 统一可售判定 + admin 派生下限 + 不可售原因枚举。
 * IAP：形状自洽即可售。STRIPE 与框架通道：需唯一 active IAP 基线、product 级字段一致、coin 相等且达派生下限；
 * 框架通道另只售 CONSUMABLE（70 §2「框架通道……权益派生与 `is_serveable` 复用上述 STRIPE 口径」）；
 * CONSUMABLE 发币与 `COIN_PERIOD_GRANT` 订阅同一 bonus 下限 `IAP bonus + ceil(IAP coin × ratio)`；
 * STRIPE 订阅（两种效果）周期非 WEEKLY / MONTHLY 即 fail-closed。
 *
 * 末相追加配对通道齐全性（A8 §1：本行 shape 不合 **且** 缺配对通道时**两值并存**，
 * 故不在本行不可售时提前返回）。
 */
export function evaluateProductServeability(
  candidate: ProductServeabilityCandidate,
  context: ProductServeabilityContext,
): ProductServeabilityResult {
  const result = evaluateExceptTierCompleteness(candidate, context);
  if (hasCompleteReadingPassTiers(candidate, context)) return result;
  return {
    ...result,
    is_serveable: false,
    validation_issues: [...result.validation_issues, 'READING_PASS_TIER_INCOMPLETE'],
  };
}

/**
 * A8 §1 `is_serveable`：框架通道行在 70 §2 统一 validator 之外，另须按 `currency_rule` 能选出价格
 * （`CHANNEL_PRICE_UNSELECTABLE`，字典 65 §9 末段，与服务端 discovery 选价剔除同口径）。IAP 与 STRIPE 行不产生。
 *
 * 刻意放在统一 validator 之外、不进卡位齐全性：C 端可售判定（`product-metadata-serveability`）不读价格，
 * 选价只在 discovery 剔除框架通道候选，IAP 行的可售与配对判定不随框架通道价格变化。
 * `priceSelectable` 由调用方按 `payment_channel_prices` 有效行求值（选价判据见 server `selectNativeChannelPrice`）。
 */
export function applyChannelPriceSelectability(
  candidate: Pick<ProductServeabilityCandidate, 'paymentMethod'>,
  result: ProductServeabilityResult,
  priceSelectable: boolean,
): ProductServeabilityResult {
  if (!isFrameworkSettlementChannel(candidate.paymentMethod) || priceSelectable) return result;
  if (result.validation_issues.includes('CHANNEL_PRICE_UNSELECTABLE')) return result;
  // 统一 validator 的原因按其求值序在前，本值追加在末（与卡位齐全性末相追加同法）
  return {
    ...result,
    is_serveable: false,
    validation_issues: [...result.validation_issues, 'CHANNEL_PRICE_UNSELECTABLE'],
  };
}

/** 第一相：形态 + 通道判据 + 派生下限，**不含**产品位齐全性（齐全性以本相结果为输入，故本相不得咨询它）。 */
function evaluateExceptTierCompleteness(
  candidate: ProductServeabilityCandidate,
  context: ProductServeabilityContext,
): ProductServeabilityResult {
  const issues: ProductValidationIssue[] = [];
  let derivedMinBonus: bigint | null = null;
  const pushIssue = (issue: ProductValidationIssue) => {
    if (!issues.includes(issue)) issues.push(issue);
  };

  // 1) 形状自洽（IAP / STRIPE 共用）——逐项枚举原因
  if (!candidate.isActive) pushIssue('INACTIVE');
  if (!isNonBlank(candidate.accessLevelId)) pushIssue('ACCESS_LEVEL_ID_MISSING');
  // A8 §1：`COIN_AMOUNT_NON_POSITIVE` 覆盖**所有要求 coin>0 的行**，即
  // `((product_type='SUBSCRIPTION' AND subscription_effect='COIN_PERIOD_GRANT') OR consumable_effect='COIN_GRANT')
  // AND coin_amount <= 0` —— 订阅侧由逐效果谓词按归一后的效果判（VIP_READING 恒 0、不归本值；
  // COIN_PERIOD_GRANT 的 coin=0 **只**归本值、不归 PRODUCT_FIELDS_INVALID），CONSUMABLE 交逐效果谓词判。
  // 判 `=== 'SUBSCRIPTION'` 而非 `!== 'CONSUMABLE'`：闭集外的 product_type 不属于契约点名的两类，
  // 它的失败条件是「product_type 跨字段不自洽」、归 PRODUCT_FIELDS_INVALID，不该借用本专用值。
  // 负金额守卫（bonus < 0）与 C 端 findProductShapeViolation 逐例等价，避免负值时 admin 误判可售、C 端拒。
  // 履约未实现与形态独立：即便跨字段不自洽，仍可能同时命中 EFFECT_FULFILLMENT_NOT_IMPLEMENTED。
  if (candidate.bonusAmount < 0n) pushIssue('PRODUCT_FIELDS_INVALID');
  let fulfillmentNotImplemented = false;
  if (candidate.productType === 'CONSUMABLE') {
    if (candidate.subscriptionPeriod !== null) {
      pushIssue('PRODUCT_FIELDS_INVALID');
      fulfillmentNotImplemented = isFulfillmentNotImplementedEffect(candidate.consumableEffect);
      if (fulfillmentNotImplemented) pushIssue(FULFILLMENT_NOT_IMPLEMENTED_ISSUE);
    } else {
      const consumableEval = findConsumableEffectViolation(candidate);
      fulfillmentNotImplemented = consumableEval.fulfillmentNotImplemented;
      for (const issue of consumableEval.issues) pushIssue(issue);
    }
  } else if (candidate.productType === 'SUBSCRIPTION') {
    for (const issue of findSubscriptionShapeViolations(candidate)) pushIssue(issue);
    fulfillmentNotImplemented = isSubscriptionFulfillmentNotImplemented(candidate);
    if (fulfillmentNotImplemented) pushIssue(FULFILLMENT_NOT_IMPLEMENTED_ISSUE);
  } else {
    pushIssue('PRODUCT_FIELDS_INVALID');
  }

  // IAP：形状自洽即可售，无派生下限
  if (candidate.paymentMethod === 'IAP') {
    return {
      is_serveable: issues.length === 0,
      derived_minimum_bonus_amount: null,
      validation_issues: issues,
      fulfillment_not_implemented: fulfillmentNotImplemented,
    };
  }

  // 框架通道只售 CONSUMABLE（70 §2）：订阅行是本行 product_type 与通道不自洽，归 PRODUCT_FIELDS_INVALID
  // （C 端根因 UNSUPPORTED_PAYMENT_METHOD 的同一投影）；不并报 STRIPE 专属的周期码。写侧已拒，这里兜存量 / 裸 SQL。
  const frameworkChannel = isFrameworkSettlementChannel(candidate.paymentMethod);
  if (frameworkChannel && candidate.productType !== 'CONSUMABLE') {
    pushIssue('PRODUCT_FIELDS_INVALID');
  }

  // A8 §1：STRIPE SUBSCRIPTION（两种效果）周期不是 WEEKLY / MONTHLY —— 只看本行，不依赖基线。
  // 周期缺失是「product_type 与 subscription_period 不自洽」，状态组合表规定只归 PRODUCT_FIELDS_INVALID，不并报本值。
  if (
    candidate.paymentMethod === 'STRIPE' &&
    candidate.productType === 'SUBSCRIPTION' &&
    candidate.subscriptionPeriod !== null &&
    !isStripeSubscriptionPeriod(candidate.subscriptionPeriod)
  ) {
    pushIssue('UNSUPPORTED_STRIPE_SUBSCRIPTION_PERIOD');
  }

  // 闭集外的通道值（软删豁免外的裸 SQL / 历史值）：C 端判 UNSUPPORTED_PAYMENT_METHOD，投影 PRODUCT_FIELDS_INVALID。
  if (!isIapPairedSettlementChannel(candidate.paymentMethod)) {
    pushIssue('PRODUCT_FIELDS_INVALID');
  }

  // STRIPE 与框架通道：需 IAP 基线派生下限（框架通道复用 STRIPE 口径，70 §2）。基线相关校验仅在自身形状自洽时才有意义地追加。
  const baseline = findUniqueIapBaseline(candidate, context.candidates);
  if (!baseline) {
    pushIssue('IAP_BASELINE_MISSING');
    return {
      is_serveable: false,
      derived_minimum_bonus_amount: null,
      validation_issues: issues,
      fulfillment_not_implemented: fulfillmentNotImplemented,
    };
  }
  if (baseline.productType !== candidate.productType) pushIssue('PRODUCT_FIELDS_MISMATCH');
  // §2.5 跨通道一致性：同 (app_id, access_level_id) 的两行 effect 相同、params 逐 key 逐值相同。
  // 归到既有的 PRODUCT_FIELDS_MISMATCH —— effect / params 是 product 级字段（§2 维度列），
  // 且 A8 §1 的 validation_issues 闭集尚无专用取值。两侧 duration_seconds 不同 = 同一产品位在两个
  // 通道卖出不同权益，用户按哪条渲染都可能被打脸。
  if (
    candidate.productType === 'CONSUMABLE' &&
    (candidate.consumableEffect !== baseline.consumableEffect ||
      !haveIdenticalEffectParams(candidate, baseline))
  ) {
    pushIssue('PRODUCT_FIELDS_MISMATCH');
  }
  // 订阅效果与周期同为 product 级字段：两行须相同（效果按归一后比较，存量旧值与新值视为同一效果）。
  // MONTHLY 放开后，STRIPE 合法周期不再隐含与基线相等，必须显式比对。
  if (
    candidate.productType === 'SUBSCRIPTION' &&
    baseline.productType === 'SUBSCRIPTION' &&
    (normalizeSubscriptionEffect(candidate.subscriptionEffect) !==
      normalizeSubscriptionEffect(baseline.subscriptionEffect) ||
      candidate.subscriptionPeriod !== baseline.subscriptionPeriod)
  ) {
    pushIssue('PRODUCT_FIELDS_MISMATCH');
  }
  if (candidate.coinAmount !== baseline.coinAmount) pushIssue('COIN_MISMATCH');

  if (!appliesThirdPartyBonusFloor(candidate)) {
    // 不发币的效果（阅读卡 / 买断 / VIP_READING）没有加赠可言（coin / bonus 恒 0 已由谓词保证），
    // 套用「STRIPE bonus ≥ IAP + ceil(coin × ratio)」只会拿 0 和 0 比，是无意义的空转；派生下限对这类效果
    // 无定义 → 保持 null。与 §2「不发币效果……本下限恒取 0，效果参数不参与派生、不因 r 变化上调」同结果。
    return {
      is_serveable: issues.length === 0,
      derived_minimum_bonus_amount: null,
      validation_issues: issues,
      fulfillment_not_implemented: fulfillmentNotImplemented,
    };
  }

  // CONSUMABLE 发币与 COIN_PERIOD_GRANT 订阅同一下限：IAP bonus + ceil(IAP coin × r)。订阅按每计费周期
  // 发放量求值、与周期长度无关（WEEKLY / MONTHLY 同一公式）；运营可上调不可低于。
  const increment = ceilProductRatio(baseline.coinAmount, context.thirdPartyBonusRatio);
  // increment===null 仅当 ratio 畸形（apps Zod 已保证不畸形）——fail-closed 对齐 C 端 INVALID_BONUS_RATIO
  if (increment === null) {
    pushIssue('BELOW_MINIMUM_BONUS');
  } else {
    derivedMinBonus = baseline.bonusAmount + increment;
    if (candidate.bonusAmount < derivedMinBonus) pushIssue('BELOW_MINIMUM_BONUS');
  }

  return {
    is_serveable: issues.length === 0,
    derived_minimum_bonus_amount: derivedMinBonus === null ? null : derivedMinBonus.toString(),
    validation_issues: issues,
    fulfillment_not_implemented: fulfillmentNotImplemented,
  };
}

/**
 * 是否套用 Stripe 加赠下限：CONSUMABLE 中除不发币效果外的行（发币；未登记效果另由形态码承接）与
 * `COIN_PERIOD_GRANT` 订阅（§2 统一派生）。`VIP_READING` / 未登记订阅效果不发币、无下限可言。
 */
function appliesThirdPartyBonusFloor(candidate: ProductServeabilityCandidate): boolean {
  if (candidate.productType === 'CONSUMABLE') {
    return !isZeroEntitlementEffect(candidate.consumableEffect);
  }
  if (candidate.productType === 'SUBSCRIPTION') {
    return normalizeSubscriptionEffect(candidate.subscriptionEffect) === 'COIN_PERIOD_GRANT';
  }
  return false;
}
